import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Consultation } from "@customTypes/consultation";
import { Pagination } from "@customTypes/index";

import { consultationQKeys } from "./queryKeys";


const select = (res: any) => {
  // console.log(res.data);
  const pagination: Pagination = {
    page: res.data.pagination.page,
    pageCount: res.data.pagination.page_count,
    pageSize: res.data.pagination.page_size,
    total: res.data.pagination.total,
  };

  if (res.data.data.length) {
    const newData: Consultation[] = [];
    for (const i in res.data.data) {
      const {
        _id,
        c_id,
        aid,
        alcohol_consumption,
        blood_pressure,
        blood_type,
        consultation_method,
        consultation_service,
        consultation_type,
        created_at,
        date,
        diagnosis,
        diet,
        doctors_note,
        donor,
        examination,
        exercising_frequency,
        files,
        follow_up,
        height,
        location,
        occupation,
        observation,
        oxygen_saturation,
        patient_id,
        physician,
        pulse_rate,
        reason,
        respiratory_rate,
        status,
        symptoms,
        temperature,
        treatment,
        updated_at,
        visibility,
        weight,
        prescription_id,
        cancellation_reason,
        recommendation,
        archived,
      } = res.data.data[i];

      newData.push({
        id: _id,
        cId: c_id,
        consultationMethod: consultation_method,
        consultationService: {
          id: consultation_service?._id,
          name: consultation_service?.name,
        },
        consultationType: consultation_type,
        prescriptionId: prescription_id,
        supportStaff: aid?.map((item: any) => ({
          id: item._id,
          fullName: item.full_name,
        })),
        bloodPressure: blood_pressure,
        date: new Date(date),
        diagnosis,
        followUp: follow_up,
        examination,
        files,
        location,
        doctorsNote: doctors_note,
        observation,
        oxygenSaturation: oxygen_saturation,
        patientId: patient_id,
        physician: {
          id: physician._id,
          fullName: physician.full_name,
        },
        pulseRate: pulse_rate,
        reason,
        respiratoryRate: respiratory_rate,
        status,
        symptoms,
        temperature,
        treatment,
        visibility,
        drinkingHabits: alcohol_consumption,
        bloodType: blood_type,
        bloodDonor: donor,
        diet,
        exerciseFrequency: exercising_frequency,
        height,
        weight,
        occupation,
        cancellationReason: cancellation_reason,
        recommendation,
        createdAt: new Date(created_at),
        updatedAt: new Date(updated_at),
        archived: !!archived,
      });
    }

    // console.log(newData);
    return {
      newData,
      pagination,
    };
  }
  return null;
};

interface QueryProps {
  patientId: string;
  withFilesOnly?: boolean;
  hasPrescription?: boolean;
  pagination?: Partial<Pagination>;
  search?: string;
  isEnabled?: boolean;
  includeArchived?: boolean;
}

export const useGetConsultationsForPatient = ({
  patientId,
  withFilesOnly = false,
  hasPrescription,
  isEnabled = true,
  pagination,
  search = "",
  includeArchived = false,
}: QueryProps) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: consultationQKeys.patientWithFilters(patientId, {
      withFilesOnly,
      ...(hasPrescription !== undefined && { hasPrescription }),
      pagination,
      ...(search && { search }),
      includeArchived,
    }),
    queryFn: () => {
      return api.get(`report/patient/${patientId}`, {
        params: {
          only_with_files: withFilesOnly,
          ...(hasPrescription !== undefined && {
            has_prescription: hasPrescription,
          }),
          ...(search && { search }),
          ...(pagination && {
            page: pagination.page,
            page_size: pagination.pageSize,
          }),
          include_archived: includeArchived,
        },
      });
    },
    select,
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes

    onError: (res: any) => {
      console.error("[From Get Consultations for Patient]", res);
      handleErrors(res, "query");
    },
  });
};
